import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import AppLogo from '../AppLogo';
import { useAuth } from '../../components/context/AuthContext';
import { useToast } from '../../components/context/ToastContext';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import { useNotifications } from '../../components/context/NotificationContext';
import Button from './Button';
import Modal from './Modal';

const AdminSidebarNavigation = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isManualInstallOpen, setIsManualInstallOpen] = useState(false);

  const { user } = useAuth();
  const { addToast } = useToast();
  const { assignedTaskCount, newBookingCount, newOrderCount, newRequestOrderCount } = useNotifications();
  const { installPromptEvent, isInstallable, isInstalled, hasDismissed, isIos, promptInstall } = useInstallPrompt();
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');

  const menuItems = [
    {
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '',
      lock: 'bi bi-lock',
      adminOnly: true,
    },
    {
      label: 'Availability',
      icon: 'Calendar',
      path: '/availability-management',
      lock: '',
      adminOnly: true,
    },
    {
      label: 'Bookings',
      icon: 'BookOpen',
      path: '/bookings-management',
      lock: '',
      badge: newBookingCount,
    },
    {
      label: 'Orders',
      icon: 'ClipboardList',
      path: '/order-management',
      lock: '',
      badge: newOrderCount + newRequestOrderCount,
    },
    {
      label: 'My Tasks',
      icon: 'ListChecks',
      path: '/my-tasks',
      lock: '',
      badge: assignedTaskCount
    },
    {
      label: 'Services / Products',
      icon: 'Scissors',
      path: '/services-management',
      lock: '',
      adminOnly: true,
    },
    ...(isAdmin ? [{
      label: 'Staff',
      icon: 'Users',
      path: '/staff-management',
      lock: ''
    }, {
      label: 'Workflows',
      icon: 'Workflow',
      path: '/workflow-management',
      lock: ''
    }] : []),
    
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      if (isIos) {
        setIsManualInstallOpen(true);
        return;
      }
      addToast('Install is not available in this browser right now. You can still use your browser’s install option from the menu.', 'info');
      return;
    }

    const accepted = await promptInstall();
    if (!accepted) {
      addToast('The install prompt was cancelled. You can try again from this button later.', 'info');
    }
  };

  const canShowInstallAction = !isInstalled && (Boolean(installPromptEvent) || isInstallable || hasDismissed || isIos);

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-card rounded-lg shadow-md hover:shadow-lg transition-all duration-250 ease-elegant hover:scale-105 active:scale-95"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} color="var(--color-primary)" />
      </button>
      <aside
        className={`fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border transition-all duration-250 ease-elegant z-40 ${
          isCollapsed ? 'w-20' : 'w-64'
        } ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="sidebar-header p-6 border-b border-border flex items-center justify-between">
            <div className={`sidebar-logo flex items-center gap-3 transition-all duration-250 ${isCollapsed ? 'justify-center' : ''}`}>
              {/* <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" size={24} color="white" />
              </div> */}
              {user.company?.logoUrl ? (
                <div className="w-12 h-12 rounded-xl overflow-hidden shadow-md flex-shrink-0">
                  <img src={user.company.logoUrl} alt={`${user.company.name} logo`} className="w-full h-full object-cover" />
                </div>
              ) : (
                <AppLogo size={28} shape="rounded" bg="brand" className="shadow-md" />
              )}
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="font-heading font-semibold text-lg text-foreground">{user.company.name}</span>
                  <span className="text-xs text-muted-foreground font-caption">{isAdmin ? 'Admin Portal' : 'Staff Portal'}</span>
                </div>
              )}
            </div>
            <button
              onClick={onToggleCollapse}
              className="hidden lg:block p-2 hover:bg-muted rounded-lg transition-colors duration-250"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
            </button>
          </div>

          <nav className="flex-1 p-4 overflow-y-auto custom-scrollbar">
            <ul className="space-y-2">
              {menuItems?.filter((item) => isAdmin || !item.adminOnly).map((item) => {
                const isActive = isActivePath(item?.path);
                return (
                  <li key={item?.path}>
                    <Link
                      to={item?.path}
                      onClick={() => setIsMobileOpen(false)}
                      className={`relative flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-250 ease-elegant ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-foreground hover:bg-muted hover:translate-x-1'
                      } ${isCollapsed ? 'justify-center' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon 
                        name={item?.icon} 
                        size={20} 
                        color={isActive ? 'var(--color-primary-foreground)' : 'currentColor'}
                      />
                     {!isCollapsed && (
                          <>
                            <span className="font-caption font-medium">
                              {item?.label}
                            </span>

                            {item?.lock && (
                              <i className={`bi ${item.lock}`} />
                            )}

                            {item?.badge > 0 && (
                              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1.5 text-[10px] font-caption font-medium text-white">
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            )}
                          </>
                        )}

                      {isCollapsed && item?.badge > 0 && (
                        <span className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[9px] font-caption font-medium text-white">
                          {item.badge > 9 ? '9+' : item.badge}
                        </span>
                      )}

                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-4 border-t border-border space-y-2">
            {canShowInstallAction && (
              <button
                onClick={handleInstallClick}
                className={`flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition-all duration-250 ease-elegant hover:bg-primary/20 hover:scale-[1.01] ${
                  isCollapsed ? 'w-full' : 'w-full'
                }`}
              >
                <Icon name="Download" size={16} />
                {!isCollapsed && <span>Install App</span>}
              </button>
            )}
            {isAdmin && (
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-4 py-3 w-full rounded-lg text-foreground hover:bg-muted transition-all duration-250 ease-elegant hover:translate-x-1 ${
                  isCollapsed ? 'justify-center' : ''
                }`}
                aria-label="Settings"
              >
                <Icon name="Settings" size={20} />
                {!isCollapsed && <span className="font-caption font-medium">Settings</span>}
              </Link>
            )}
          </div>
        </div>
      </aside>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background z-30"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
      <Modal
        isOpen={isManualInstallOpen}
        onClose={() => setIsManualInstallOpen(false)}
        title="Install Claw"
      >
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground font-caption">
            On iPhone or iPad, Claw is installed from Safari rather than an automatic browser prompt.
          </p>
          <ol className="space-y-4">
            {[
              ['Compass', 'Open Claw in Safari'],
              ['Share', 'Tap the Share button'],
              ['SquarePlus', 'Choose Add to Home Screen'],
              ['Check', 'Tap Add to confirm'],
            ].map(([icon, instruction], index) => (
              <li key={instruction} className="flex items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-caption font-semibold">
                  {index + 1}
                </span>
                <Icon name={icon} size={18} className="flex-shrink-0 text-muted-foreground" />
                <span className="text-sm text-foreground font-caption">{instruction}</span>
              </li>
            ))}
          </ol>
          <Button fullWidth onClick={() => setIsManualInstallOpen(false)}>Done</Button>
        </div>
      </Modal>
    </>
  );
};

export default AdminSidebarNavigation;
